import { STATUS } from "@/constants/statuses";
import { transformMeta } from "@apps/iloom_customization/helpers/transformMeta";
import { ProductService } from "@apps/iloom_customization/modules/products/services/ProductService";
import { ReviewsApiService } from "@apps/iloom_customization/reviews/api_services/ReviewsApiService";
import IloomAttributesFiltersStore from "@apps/iloom_customization/reviews/stores/IloomAttributesFiltersStore";
import ReviewsStore from "@apps/iloom_customization/reviews/stores/ReviewsStore";
import { iloomCustomizationApplicationLoggerToken } from "@apps/iloom_customization/tokens";
import { GetReviewsRequestParamsDTO } from "@apps/reviews/services/ReviewsServiceBase";
import VotesStore from "@apps/reviews/stores/VotesStore";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import { applyTransaction } from "@datorama/akita";
import { omit } from "rambda";
import { catchError, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

@singleton()
export class IloomReviewsService {
    constructor(
        private readonly reviewsApiService: ReviewsApiService,
        private readonly reviewsStore: ReviewsStore,
        private readonly votesStore: VotesStore,
        private readonly iloomAttributesFiltersStore: IloomAttributesFiltersStore,
        private readonly productService: ProductService,
        private readonly wishlistProductStatusesService: WishlistProductStatusesService,

        @inject(iloomCustomizationApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getReviews(queryParams?: GetReviewsRequestParamsDTO, isLoadMore = false) {
        this.reviewsStore.update((state) => {
            if (isLoadMore && queryParams?.offset) {
                state.loadMoreStatus = STATUS.LOADING;
            } else {
                state.meta.offset = queryParams?.offset || 0;
                state.status = STATUS.LOADING;
            }
        });

        return this.reviewsApiService.getReviews(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug("ReviewsService.getReviews is errored", {
                    queryParams,
                    error,
                });
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    // In Iloom, only active products are displayed
                    if (
                        item.product?.id &&
                        !item.product?.isDraft &&
                        !item.product?.isDeleted
                    ) {
                        this.productService.addProductForFetching(
                            item.product?.id
                        );
                    }
                });
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    if (item.product?.id)
                        this.wishlistProductStatusesService.addProductIdForFetching(
                            item.product.id
                        );
                });
            }),
            tap((value) => {
                applyTransaction(() => {
                    this.reviewsStore.update((state) => {
                        const meta = transformMeta(omit(["items"], value));
                        const items =
                            isLoadMore && queryParams?.offset
                                ? [...state.reviews, ...value.items]
                                : value.items;

                        // const productIds = value.items.map(
                        //     (item) => item.product?.id
                        // ) as number[];

                        state.reviews = items;
                        // state.productIds = productIds;
                        state.meta = meta;
                        state.status = STATUS.READY;
                        state.loadMoreStatus = STATUS.READY;
                    });

                    this.votesStore.add(
                        value.items.map((item) => ({
                            reviewId: item.id,
                            votes: item.votes,
                            isCustomerVoted: item.isCustomerVoted,
                            status: STATUS.VIRGIN,
                        }))
                    );
                });
            })
        );
    }

    getFeaturedReviews(queryParams?: GetReviewsRequestParamsDTO) {
        this.reviewsStore.update((state) => {
            state.featuredStatus = STATUS.LOADING;
        });

        return this.reviewsApiService.getReviews(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "ReviewsService.getFeaturedReviews is errored",
                    {
                        queryParams,
                        error,
                    }
                );
                applyTransaction(() => {
                    this.reviewsStore.setError(error);
                    this.reviewsStore.setStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    // In Iloom, only active products are displayed
                    if (
                        item.product?.id &&
                        !item.product?.isDraft &&
                        !item.product?.isDeleted
                    ) {
                        this.productService.addProductForFetching(
                            item.product?.id
                        );
                    }
                });
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    if (item.product?.id)
                        this.wishlistProductStatusesService.addProductIdForFetching(
                            item.product?.id
                        );
                });
            }),
            tap((value) => {
                applyTransaction(() => {
                    this.reviewsStore.update((state) => {
                        // const productIds = value.items.map(
                        //     (item) => item.product?.id
                        // ) as number[];

                        state.featuredReviews = value.items;
                        // state.featuredProductIds = productIds;
                        state.featuredStatus = STATUS.READY;
                    });
                });
            })
        );
    }

    getIllomAttributeFilters() {
        this.iloomAttributesFiltersStore.update((state) => {
            state.status = STATUS.LOADING;
        });

        return this.reviewsApiService.getIllomAttributeFilters().pipe(
            tap((value) => {
                applyTransaction(() => {
                    this.iloomAttributesFiltersStore.update((state) => {
                        state.data = value;
                        state.status = STATUS.READY;
                    });
                });
            })
        );
    }
}
